"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.vatReturn = vatReturn;
const money_1 = require("../money");
const NON_TAXABLE = ['zero', 'exempt', 'out_of_scope'];
/** خانة المستند واتجاه أثره الطبيعى؛ العكس عليه يُخصم منها. */
const PLACEMENT = {
    sales_invoice: { kind: 'sale', side: 'credit' },
    sales_debit_note: { kind: 'debit_note', side: 'credit' },
    sales_credit_note: { kind: 'credit_note', side: 'debit' },
    purchase_invoice: { kind: 'purchase', side: 'debit' },
    // إشعارات المشتريات تُصحح خانة المشتريات نفسها: الدائن من المورد يزيدها والمدين لنا
    // (مرتجع) يخصم منها — BLOCKED-TAX حتى يؤكد المستشار موضعها فى الإقرار
    purchase_credit_note: { kind: 'purchase', side: 'debit' },
    purchase_debit_note: { kind: 'purchase', side: 'debit' },
    expense: { kind: 'purchase', side: 'debit' },
};
const zeroFigure = () => ({ base: money_1.Money.zero(), tax: money_1.Money.zero() });
function addTo(figure, base, tax) {
    figure.base = figure.base.add(base);
    figure.tax = figure.tax.add(tax);
}
function byTreatmentAndRate(a, b) {
    if (a.treatment !== b.treatment)
        return a.treatment < b.treatment ? -1 : 1;
    // الأعلى نسبة أولاً — كما تُقرأ عادة
    return money_1.Money.of(b.ratePercent).cmp(money_1.Money.of(a.ratePercent));
}
function vatReturn(lines) {
    const sales = new Map();
    const purchases = new Map();
    const other = new Map();
    const documents = new Map();
    for (const line of lines) {
        const snapshot = line.tax;
        // الخصم من المنبع ليس قيمة مضافة — له تقريره
        if (snapshot.treatment === 'withholding')
            continue;
        const debit = money_1.Money.of(line.debit);
        const credit = money_1.Money.of(line.credit);
        const base = money_1.Money.of(snapshot.base);
        const tax = money_1.Money.of(snapshot.amount);
        const rate = money_1.Money.of(snapshot.ratePercent).normalize().toString();
        const placement = PLACEMENT[line.sourceType];
        let rowKey;
        let kind;
        let sign = 1;
        if (!placement) {
            kind = 'other';
            rowKey = 'other:' + line.sourceType + ':' + snapshot.treatment + ':' + rate;
            const row = other.get(rowKey) ?? {
                key: rowKey,
                sourceType: line.sourceType,
                treatment: snapshot.treatment,
                ratePercent: rate,
                credit: zeroFigure(),
                debit: zeroFigure(),
            };
            addTo(credit.isPositive() ? row.credit : row.debit, base, tax);
            other.set(rowKey, row);
            if (!credit.isPositive())
                sign = -1;
        }
        else {
            kind = placement.kind;
            const onNaturalSide = placement.side === 'credit' ? credit.isPositive() : debit.isPositive();
            sign = onNaturalSide ? 1 : -1;
            const signedBase = sign === 1 ? base : base.negate();
            const signedTax = sign === 1 ? tax : tax.negate();
            if (kind === 'purchase') {
                rowKey =
                    'purchases:' +
                        snapshot.treatment +
                        ':' +
                        rate +
                        ':' +
                        (snapshot.recoverable ? 'recoverable' : 'non_recoverable');
                const row = purchases.get(rowKey) ?? {
                    key: rowKey,
                    treatment: snapshot.treatment,
                    ratePercent: rate,
                    recoverable: snapshot.recoverable,
                    purchases: zeroFigure(),
                };
                addTo(row.purchases, signedBase, signedTax);
                purchases.set(rowKey, row);
            }
            else {
                rowKey = 'sales:' + snapshot.treatment + ':' + rate;
                const row = sales.get(rowKey) ?? {
                    key: rowKey,
                    treatment: snapshot.treatment,
                    ratePercent: rate,
                    taxable: !NON_TAXABLE.includes(snapshot.treatment),
                    sales: zeroFigure(),
                    creditNotes: zeroFigure(),
                    debitNotes: zeroFigure(),
                    net: zeroFigure(),
                };
                const column = kind === 'sale' ? row.sales : kind === 'credit_note' ? row.creditNotes : row.debitNotes;
                addTo(column, signedBase, signedTax);
                if (kind === 'credit_note')
                    addTo(row.net, signedBase.negate(), signedTax.negate());
                else
                    addTo(row.net, signedBase, signedTax);
                sales.set(rowKey, row);
            }
        }
        const documentKey = line.journalId + '|' + rowKey;
        const document = documents.get(documentKey) ?? {
            rowKey,
            kind,
            journalId: line.journalId,
            journalNumber: line.journalNumber,
            postingDate: line.postingDate,
            sourceType: line.sourceType,
            journalSourceType: line.journalSourceType,
            sourceId: line.sourceId,
            reference: line.reference,
            base: money_1.Money.zero(),
            tax: money_1.Money.zero(),
        };
        document.base = document.base.add(sign === 1 ? base : base.negate());
        document.tax = document.tax.add(sign === 1 ? tax : tax.negate());
        documents.set(documentKey, document);
    }
    const salesRows = [...sales.values()].sort((a, b) => Number(b.taxable) - Number(a.taxable) || byTreatmentAndRate(a, b));
    const purchaseRows = [...purchases.values()].sort((a, b) => Number(b.recoverable) - Number(a.recoverable) || byTreatmentAndRate(a, b));
    const otherRows = [...other.values()].sort((a, b) => (a.sourceType === b.sourceType ? 0 : a.sourceType < b.sourceType ? -1 : 1) ||
        byTreatmentAndRate(a, b));
    const taxable = salesRows.filter((row) => row.taxable);
    const nonTaxable = salesRows.filter((row) => !row.taxable);
    const outputTax = money_1.Money.sum(salesRows.map((row) => row.net.tax));
    const recoverableInputTax = money_1.Money.sum(purchaseRows.filter((row) => row.recoverable).map((row) => row.purchases.tax));
    const nonRecoverableInputTax = money_1.Money.sum(purchaseRows.filter((row) => !row.recoverable).map((row) => row.purchases.tax));
    const netTax = outputTax.sub(recoverableInputTax);
    return {
        sales: salesRows,
        purchases: purchaseRows,
        other: otherRows,
        documents: [...documents.values()].sort((a, b) => (a.postingDate === b.postingDate ? 0 : a.postingDate < b.postingDate ? -1 : 1) ||
            (a.journalNumber ?? '').localeCompare(b.journalNumber ?? '')),
        outputTax,
        taxableSalesBase: money_1.Money.sum(taxable.map((row) => row.net.base)),
        nonTaxableSalesBase: money_1.Money.sum(nonTaxable.map((row) => row.net.base)),
        purchasesBase: money_1.Money.sum(purchaseRows.map((row) => row.purchases.base)),
        recoverableInputTax,
        nonRecoverableInputTax,
        netTax,
        position: netTax.isPositive() ? 'payable' : netTax.isNegative() ? 'refundable' : 'nil',
    };
}
//# sourceMappingURL=vat.js.map